export type ScoringMode = "committed" | "code-coverage" | "weighted";
export type ExpirationPolicyType = "never" | "time" | "change" | "combined";

export interface ExpirationConfig {
  policy: ExpirationPolicyType;
  duration?: number; // days
  threshold?: number; // 0-1 (e.g., 0.5 for 50%)
}

export interface WeightConfig {
  blame: number; // default 0.5
  commit: number; // default 0.5
}

export type HotspotMode = "personal" | "team";
export type HotspotRiskLevel = "critical" | "high" | "medium" | "low";

export interface CliOptions {
  mode: ScoringMode;
  user?: string | string[];
  expiration: ExpirationConfig;
  html: boolean;
  weights: WeightConfig;
  repoPath: string;
  team?: boolean;
  contributorsPerFile?: boolean;
  hotspot?: HotspotMode;
  since?: number; // days for hotspot analysis
  demo?: boolean;
}

export interface UserIdentity {
  name: string;
  email: string;
}

export interface FileScore {
  type: "file";
  path: string;
  lines: number;
  score: number;
  isWritten?: boolean;
  blameScore?: number;
  commitScore?: number;
  isExpired?: boolean;
  lastTouchDate?: Date;
}

export interface FolderScore {
  type: "folder";
  path: string;
  lines: number;
  score: number;
  fileCount: number;
  readCount?: number;
  children: TreeNode[];
}

export type TreeNode = FileScore | FolderScore;

export interface CommitInfo {
  hash: string;
  date: Date;
  addedLines: number;
  deletedLines: number;
  fileSizeAtCommit: number;
}

export const DEFAULT_WEIGHTS: WeightConfig = {
  blame: 0.5,
  commit: 0.5,
};

export const DEFAULT_EXPIRATION: ExpirationConfig = {
  policy: "never",
};

// ── Shared Constants ──

/** Hotspot risk thresholds (risk score 0-1) */
export const HOTSPOT_RISK_THRESHOLDS = {
  critical: 0.6,
  high: 0.4,
  medium: 0.2,
} as const;

/** Contributor count thresholds for bus factor risk classification */
export const COVERAGE_RISK_THRESHOLDS = {
  risk: 1, // <= 1 contributor
  moderate: 3, // <= 3 contributors
} as const;

/** Default time window for hotspot analysis (days) */
export const DEFAULT_HOTSPOT_WINDOW = 90;

/** Bus factor target: fraction of files that must be covered */
export const BUS_FACTOR_TARGET = 0.5;

// ── Multi-User Comparison Types ──

export type RiskLevel = "safe" | "moderate" | "risk";

export interface UserScore {
  user: UserIdentity;
  score: number;
  isWritten?: boolean;
}

export interface MultiUserFileScore {
  type: "file";
  path: string;
  lines: number;
  score: number;
  userScores: UserScore[];
}

export interface MultiUserFolderScore {
  type: "folder";
  path: string;
  lines: number;
  score: number;
  fileCount: number;
  userScores: UserScore[];
  children: MultiUserTreeNode[];
}

export type MultiUserTreeNode = MultiUserFileScore | MultiUserFolderScore;

export interface UserSummary {
  user: UserIdentity;
  writtenCount: number;
  overallScore: number;
}

export interface MultiUserResult {
  tree: MultiUserFolderScore;
  repoName: string;
  users: UserIdentity[];
  mode: string;
  totalFiles: number;
  userSummaries: UserSummary[];
}

// ── Team Coverage Types ──

export interface CoverageFileScore {
  type: "file";
  path: string;
  lines: number;
  contributorCount: number;
  contributors: string[];
  riskLevel: RiskLevel;
}

export interface CoverageFolderScore {
  type: "folder";
  path: string;
  lines: number;
  fileCount: number;
  avgContributors: number;
  busFactor: number;
  riskLevel: RiskLevel;
  children: CoverageTreeNode[];
}

export type CoverageTreeNode = CoverageFileScore | CoverageFolderScore;

export interface TeamCoverageResult {
  tree: CoverageFolderScore;
  repoName: string;
  totalContributors: number;
  totalFiles: number;
  riskFiles: CoverageFileScore[];
  overallBusFactor: number;
}

// ── Unified Dashboard Types ──

export interface UnifiedData {
  repoName: string;
  userName: string;
  scoring: {
    committed: FamiliarityResult;
    codeCoverage: FamiliarityResult;
    weighted: FamiliarityResult;
  };
  coverage: TeamCoverageResult;
  hotspot: HotspotResult;
  hotspotTeamFamiliarity: Record<string, number>;
  multiUser: MultiUserResult;
}

export interface FamiliarityResult {
  tree: FolderScore;
  repoName: string;
  userName: string;
  mode: string;
  writtenCount: number;
  totalFiles: number;
}

// ── Hotspot Analysis Types ──

export interface HotspotFileScore {
  path: string;
  lines: number;
  familiarity: number;
  changeFrequency: number;
  lastChanged: Date | null;
  risk: number;
  riskLevel: HotspotRiskLevel;
}

export interface HotspotResult {
  files: HotspotFileScore[];
  repoName: string;
  userName?: string;
  hotspotMode: HotspotMode;
  timeWindow: number;
  summary: {
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
}
